import type {
    AutoLayoutJob,
    AutoLayoutJobUid,
    AutoLayoutIterationClientData,
    DocumentUid,
    UserUid,
    OrganizationUid,
    NodeUid,
    AutoLayoutJobConfiguration,
    AutoLayoutPreflightAnomaly,
    AutoLayoutJobMetadataError,
} from "@buildwithflux/models";
import type {StoreApi} from "zustand";

/**
 * Client-side job states for auto-layout jobs.
 */
export enum AutoLayoutClientJobState {
    idle = "idle",
    waiting = "waiting",
    pausing = "pausing",
    paused = "paused",
    resuming = "resuming",
    running = "running",
    noCredits = "noCredits",
    noCreditsAwaitingConsent = "noCreditsAwaitingConsent",
    complete = "complete",
    canceling = "canceling",
    canceled = "canceled",
    applying = "applying",
    applied = "applied",
    error = "error",
}

/**
 * Complete auto-layout job state as stored in the centralized store.
 */
export interface AutoLayoutClientJob {
    // Job identification
    jobUid: AutoLayoutJobUid | undefined;
    readonly projectUid: DocumentUid;
    readonly userUid: UserUid;
    readonly organizationUid: OrganizationUid | undefined;
    readonly layoutNodeUid: NodeUid;
    readonly config: AutoLayoutJobConfiguration;

    // Job runtime state
    state: AutoLayoutClientJobState;
    creditsUsed: number;
    iterations: AutoLayoutIterationClientData[];
    currentIterationIndex: number;
    loadingIteration: boolean;
    preflightAnomalies: AutoLayoutPreflightAnomaly[] | undefined;
    metadataErrors: AutoLayoutJobMetadataError[];
    startTime: Date | undefined;
}

/**
 * State shape of the centralized auto-layout store.
 */
export interface AutoLayoutStoreState {
    currentJob: AutoLayoutClientJob | undefined;
    sliderPosition: number;
}

/**
 * Interface for the centralized auto-layout store service.
 * Manages all auto-layout job state and shared data through a clean service API.
 * The store is always defined, but currentJob can be undefined when no job is active.
 */
export interface AutoLayoutStoreService {
    /**
     * Gets the underlying Zustand store for subscriptions.
     * Store is always defined, but currentJob within it can be undefined.
     */
    getStore(): StoreApi<AutoLayoutStoreState>;

    // Job existence queries
    /**
     * Checks if there is an active job in the store.
     */
    hasActiveJob(): boolean;

    /**
     * Gets a snapshot of the current job state.
     * Returns undefined if no job is active.
     */
    getCurrentJob(): AutoLayoutClientJob | undefined;

    // Job lifecycle management
    /**
     * Starts a new job with the given properties.
     * Clears any existing job and creates a new one.
     */
    startJob(
        projectUid: DocumentUid,
        userUid: UserUid,
        organizationUid: OrganizationUid | undefined,
        layoutNodeUid: NodeUid,
        config: AutoLayoutJobConfiguration,
        jobUid?: AutoLayoutJobUid,
    ): void;

    /**
     * Creates a new job from backend job data.
     * Useful when loading an existing job from the backend.
     */
    loadFromBackendJob(backendJob: AutoLayoutJob, layoutNodeUid: NodeUid): AutoLayoutClientJob;

    /**
     * Clears the current job and slider position from the store.
     */
    resetStore(): void;

    // Job UID management
    /**
     * Sets the job UID for the current job.
     * Throws error if no active job exists.
     */
    setJobUid(jobUid: AutoLayoutJobUid | undefined): void;

    // Job state management
    /**
     * Sets the client job state.
     * Throws error if no active job exists.
     */
    setState(state: AutoLayoutClientJobState): void;

    /**
     * Gets the current job state.
     * Returns undefined if no active job exists.
     */
    getCurrentState(): AutoLayoutClientJobState | undefined;

    // Credits management
    /**
     * Gets the credits used by the current job.
     * Returns 0 if no active job exists.
     */
    getCreditsUsed(): number;

    /**
     * Updates the credits used by the current job.
     * Throws error if no active job exists.
     */
    updateCreditsUsed(credits: number): void;

    // Iteration management
    /**
     * Gets all iterations for the current job.
     * Returns empty array if no active job exists.
     */
    getIterations(): AutoLayoutIterationClientData[];

    /**
     * Adds new iterations to the current job.
     * Throws error if no active job exists.
     */
    addIterations(iterationDataArray: AutoLayoutIterationClientData[]): void;

    /**
     * Updates an existing iteration in the current job.
     * Throws error if no active job exists.
     */
    updateIteration(iterationData: AutoLayoutIterationClientData): void;

    /**
     * Gets the current iteration index.
     */
    getCurrentIterationIndex(): number | undefined;

    /**
     * Sets the current iteration index.
     * Throws error if no active job exists or index is out of bounds.
     */
    setCurrentIterationIndex(index: number): void;

    /**
     * Sets the loading iteration state.
     * Throws error if no active job exists.
     */
    setLoadingIteration(loading: boolean): void;

    // Backend synchronization
    /**
     * Synchronizes the current job with backend job data.
     * Throws error if no active job exists.
     */
    syncFromBackend(backendJob: AutoLayoutJob): void;

    // Error management
    /**
     * Gets metadata errors for the current job.
     * Returns empty array if no active job exists.
     */
    getMetadataErrors(): ReadonlyArray<AutoLayoutJobMetadataError>;

    /**
     * Sets metadata errors for the current job.
     * Throws error if no active job exists.
     */
    setMetadataErrors(errors: AutoLayoutJobMetadataError[]): void;

    // Preflight anomalies
    /**
     * Gets preflight anomalies for the current job.
     * Returns undefined if no active job exists or no anomalies set.
     */
    getPreflightAnomalies(): AutoLayoutPreflightAnomaly[] | undefined;

    // Slider position management (shared state)
    /**
     * Gets the current slider position.
     */
    getSliderPosition(): number;

    /**
     * Sets the current slider position.
     */
    setSliderPosition(position: number): void;

    // State queries (convenience methods)
    /**
     * Checks if the current job can be paused.
     * Returns false if no active job exists.
     */
    canPause(): boolean;

    /**
     * Checks if the current job can be resumed.
     * Returns false if no active job exists.
     */
    canResume(): boolean;

    /**
     * Checks if the current job is active.
     * Returns false if no active job exists.
     */
    isActive(): boolean;

    /**
     * Checks if the current job is in a terminal state.
     * Returns false if no active job exists.
     */
    isTerminal(): boolean;
}
